// State location: state.ui.procedureRunPlans
import produce from 'immer';

import {BenchlingAction} from 'redux-stores/Action';

import {
  ProcedureRunPlanUIActionType,
  ProcedureRunTab,
  ProcedureRunOrPlanStateSummary,
  ProcedureRunPlanDesignStateSummary,
} from './ProcedureRunPlanUIReduxActions';

export interface SingleStudyProcedureRunPlanUIState {
  selectedProcedureRun: ProcedureRunOrPlanStateSummary;
  selectedProcedureRunPlan: ProcedureRunOrPlanStateSummary;
  currentProcedureRunTab: ProcedureRunTab;
  promotingPlanIds: Array<string>;
  procedureRunPlanDesignStateByPlanId: {[planId: string]: ProcedureRunPlanDesignStateSummary};
  /**
   * Current edit mode for the selected plan. This is kept in sync with
   * procedureRunPlanDesignStateByPlanId[selectedPlanId].isInEditMode when persisted
   * state exists. Having both allows:
   * 1. Edit mode to be set before design state is persisted (user enters edit mode immediately)
   * 2. Edit mode to be restored when switching between plans (from persisted state)
   */
  isInEditMode: boolean;
}

export interface ProcedureRunPlansUIState {
  byStudyId: {[studyId: string]: SingleStudyProcedureRunPlanUIState};
}

export const PROCEDURE_RUN_PLANS_UI_INITIAL_STATE: ProcedureRunPlansUIState = {
  byStudyId: {},
};

function getDefaultSingleStudyState(
  defaultTab: ProcedureRunTab = ProcedureRunTab.OVERVIEW
): SingleStudyProcedureRunPlanUIState {
  return {
    selectedProcedureRun: {id: null, name: null},
    selectedProcedureRunPlan: {id: null, name: null, procedureId: null},
    currentProcedureRunTab: defaultTab,
    promotingPlanIds: [],
    procedureRunPlanDesignStateByPlanId: {},
    isInEditMode: false,
  };
}

export default function reduceProcedureRunPlansUI(
  state: ProcedureRunPlansUIState = PROCEDURE_RUN_PLANS_UI_INITIAL_STATE,
  action: BenchlingAction
): ProcedureRunPlansUIState {
  function clearSelection(studyState: SingleStudyProcedureRunPlanUIState): void {
    studyState.selectedProcedureRun = {id: null, name: null};
    studyState.selectedProcedureRunPlan = {id: null, name: null, procedureId: null};
    studyState.currentProcedureRunTab = ProcedureRunTab.OVERVIEW;
    studyState.isInEditMode = false;
  }

  switch (action.type) {
    case ProcedureRunPlanUIActionType.SET_SELECTED_PROCEDURE_RUN: {
      const {studyId, procedureRun} = action;
      return produce(state, (draft) => {
        const studyState =
          draft.byStudyId[studyId] ?? (draft.byStudyId[studyId] = getDefaultSingleStudyState());
        studyState.selectedProcedureRun = procedureRun;
      });
    }
    case ProcedureRunPlanUIActionType.SET_SELECTED_PROCEDURE_RUN_PLAN: {
      const {studyId, procedureRunPlan} = action;
      return produce(state, (draft) => {
        const studyState =
          draft.byStudyId[studyId] ??
          (draft.byStudyId[studyId] = getDefaultSingleStudyState(ProcedureRunTab.DESIGN));
        studyState.selectedProcedureRunPlan = procedureRunPlan;
        // Restore edit mode from persisted state if available
        studyState.isInEditMode =
          procedureRunPlan.id != null
            ? (studyState.procedureRunPlanDesignStateByPlanId[procedureRunPlan.id]?.isInEditMode ?? false)
            : false;
      });
    }
    case ProcedureRunPlanUIActionType.SET_CURRENT_PROCEDURE_RUN_TAB: {
      const {studyId, tab} = action;
      return produce(state, (draft) => {
        const studyState =
          draft.byStudyId[studyId] ?? (draft.byStudyId[studyId] = getDefaultSingleStudyState());
        studyState.currentProcedureRunTab = tab;
      });
    }
    case ProcedureRunPlanUIActionType.ADD_PROMOTING_PLAN_ID: {
      const {studyId, planId} = action;
      return produce(state, (draft) => {
        const studyState =
          draft.byStudyId[studyId] ?? (draft.byStudyId[studyId] = getDefaultSingleStudyState());
        if (!studyState.promotingPlanIds.includes(planId)) {
          studyState.promotingPlanIds.push(planId);
        }
      });
    }
    case ProcedureRunPlanUIActionType.REMOVE_PROMOTING_PLAN_ID: {
      const {studyId, planId} = action;
      return produce(state, (draft) => {
        const studyState = draft.byStudyId[studyId] ?? null;
        if (studyState != null) {
          studyState.promotingPlanIds = studyState.promotingPlanIds.filter((id) => id !== planId);
        }
      });
    }
    case ProcedureRunPlanUIActionType.SET_PROCEDURE_RUN_PLAN_DESIGN_STATE: {
      const {studyId, procedureRunPlanId, designState} = action;
      return produce(state, (draft) => {
        const studyState =
          draft.byStudyId[studyId] ??
          (draft.byStudyId[studyId] = getDefaultSingleStudyState(ProcedureRunTab.DESIGN));
        studyState.procedureRunPlanDesignStateByPlanId[procedureRunPlanId] = designState;
        // Keep study-level edit mode in sync if this is the selected plan
        if (studyState.selectedProcedureRunPlan.id === procedureRunPlanId) {
          studyState.isInEditMode = designState.isInEditMode;
        }
      });
    }
    case ProcedureRunPlanUIActionType.SET_IS_IN_EDIT_MODE: {
      const {studyId, isInEditMode} = action;
      return produce(state, (draft) => {
        const studyState =
          draft.byStudyId[studyId] ?? (draft.byStudyId[studyId] = getDefaultSingleStudyState());
        studyState.isInEditMode = isInEditMode;
        // Keep persisted state in sync if it exists for the selected plan
        const selectedPlanId = studyState.selectedProcedureRunPlan.id;
        if (selectedPlanId != null) {
          const existingDesignState = studyState.procedureRunPlanDesignStateByPlanId[selectedPlanId];
          if (existingDesignState != null) {
            existingDesignState.isInEditMode = isInEditMode;
          }
        }
      });
    }
    case ProcedureRunPlanUIActionType.CLEAR_PROCEDURE_RUN_PLAN_SELECTION: {
      const {studyId} = action;
      return produce(state, (draft) => {
        const studyState = draft.byStudyId[studyId] ?? null;
        if (studyState != null) {
          clearSelection(studyState);
        }
      });
    }
    case ProcedureRunPlanUIActionType.RESET_STUDY_STATE: {
      const {studyId} = action;
      return produce(state, (draft) => {
        delete draft.byStudyId[studyId];
      });
    }
    default:
      return state;
  }
}
